/**
 * Authentication Service for Pinpoint Web App
 *
 * Uses the bridge client to access Chrome Identity API through the extension.
 * All OAuth token management is done via the bridge → service worker.
 */

import { bridge } from './bridge-client';
import { API_URL } from './config';

// ============================================================================
// Types
// ============================================================================

export interface AppConfig {
  freeTrialLimit: number;
  anonymousTrialLimit: number;
  feedbackUrl: string;
}

export interface AnonStatus {
  remaining: number;
  limit: number;
}

export interface UserInfo {
  email: string;
  name: string;
  token: string;
}

export type RegularPlan = 'essential' | 'professional';
export type StudentPlan = 'starter' | 'scholar';
export type Plan = RegularPlan | StudentPlan;

export type UserType = 'law_student' | 'paralegal' | 'lawyer' | 'researcher' | 'other';

export interface StripeSubscriptionData {
  plan: Plan;
  planName: string;
  billingInterval: 'month' | 'year';
  status: string;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  cancelAt: string | null;
  trialEnd: string | null;
  amountDisplay: string;
}

export interface UserStatus {
  email: string;
  name: string | null;
  userType: UserType | null;
  onboardingComplete: boolean;
  subscribed: boolean;
  plan: Plan | null;
  billingInterval: 'month' | 'year' | null;
  messageCount: number;
  trialRemaining: number | null;
  trialLimit: number | null;
  paidRemaining: number | null;
  paidLimit: number | null;
  studentVerified: boolean;
  cancelledAt: string | null;
  // Similar cases usage
  similarCasesCount: number;
  similarCasesLimit: number;
  similarCasesRemaining: number;
  // Live Stripe data (null if not subscribed or Stripe unavailable)
  stripe: StripeSubscriptionData | null;
}

export interface SimilarCase {
  title: string;
  url: string;
  highlight: string;
}

// Feature value can be boolean, number, or string (e.g. "Unlimited")
export type FeatureValue = boolean | number | string;

export interface PricingFeature {
  name: string;
  essential: FeatureValue;
  professional: FeatureValue;
}

export interface PricingTier {
  id: string;
  name: string;
  monthlyPrice: number;
  annualMonthlyPrice: number | null;  // effective monthly rate for annual plan
  messages: string;
  stripeMonthlyLink: string;
  stripeAnnualLink: string | null;
  monthlyPriceUsd: number;
  annualMonthlyPriceUsd: number | null;
  stripeMonthlyLinkUsd: string;
  stripeAnnualLinkUsd: string | null;
}

export interface StudentPricingTier {
  id: string;
  name: string;
  monthlyPrice: number;
  messages: string;
  stripeLink: string;
  stripeLinkUsd: string;
}

export interface StudentPricingFeature {
  name: string;
  starter: FeatureValue;
  scholar: FeatureValue;
}

export interface PricingConfig {
  tiers: PricingTier[];
  features: PricingFeature[];
  studentTiers: StudentPricingTier[];
  studentFeatures: StudentPricingFeature[];
  enterpriseEmail: string;
}

// ============================================================================
// Token Cache
// ============================================================================

let cachedToken: string | null = null;

/**
 * Get the current user's Google OAuth token via bridge
 */
export async function getAuthToken(interactive = false): Promise<string | null> {
  const token = await bridge.getAuthToken(interactive);
  cachedToken = token;
  return token;
}

/**
 * Clear token cache (useful when token expires)
 */
export function clearTokenCache(): void {
  cachedToken = null;
}

// ============================================================================
// User Authentication
// ============================================================================

/**
 * Get user info from the token
 */
export async function getUserInfo(): Promise<UserInfo | null> {
  const token = await getAuthToken(false);
  if (!token) return null;

  try {
    const response = await fetch('https://www.googleapis.com/oauth2/v2/userinfo', {
      headers: { 'Authorization': `Bearer ${token}` }
    });

    if (!response.ok) {
      cachedToken = null;
      return null;
    }

    const data = await response.json();

    return {
      email: data.email,
      name: data.name || data.given_name || '',
      token
    };
  } catch {
    return null;
  }
}

/**
 * Sign in the user (interactive - will show Google sign-in popup if needed)
 *
 * On Google Scholar pages, Chrome may return an ambient Google token from the
 * browser session instead of our extension's OAuth token. This token fails
 * userinfo validation. When that happens, we sign out first (which calls
 * removeCachedAuthToken via the existing SIGN_OUT bridge handler) and retry,
 * forcing Chrome to show the proper OAuth consent flow.
 */
export async function signIn(): Promise<UserInfo | null> {
  clearTokenCache();

  const token = await getAuthToken(true);
  if (!token) return null;

  const userInfo = await getUserInfo();
  if (userInfo) return userInfo;

  // Token was returned but userinfo failed - likely an ambient/invalid token.
  // Use signOut() to clear Chrome's cached token via the existing SIGN_OUT handler,
  // then retry to force the real OAuth consent flow.
  await bridge.signOut();
  clearTokenCache();

  const retryToken = await getAuthToken(true);
  if (!retryToken) return null;

  return getUserInfo();
}

/**
 * Sign out the user via bridge
 */
export async function signOut(): Promise<void> {
  const token = cachedToken || await getAuthToken(false);
  clearTokenCache();

  // Revoke token with Google (allows switching accounts)
  if (token) {
    try {
      await fetch(`https://accounts.google.com/o/oauth2/revoke?token=${token}`);
    } catch {
      // Revoke may fail if offline, continue anyway
    }
  }

  // Clear tokens via bridge → service worker
  await bridge.signOut();

  // Clear storage via bridge
  await bridge.storageRemove(['authToken', 'userEmail']);
}

/**
 * Check if user is signed in (non-interactive check)
 */
export async function isSignedIn(): Promise<boolean> {
  const token = await getAuthToken(false);
  return token !== null;
}

// ============================================================================
// Premium Features Helper
// ============================================================================

/**
 * Check if user has access to premium features (time tracking, similar cases)
 * Includes trial users and professional subscribers
 */
export function hasPremiumFeatures(status: UserStatus | null): boolean {
  if (!status) return false;
  // Trial users get premium features
  if (!status.subscribed && status.trialRemaining !== null && status.trialRemaining > 0) {
    return true;
  }
  // Professional subscribers
  return status.plan === 'professional';
}

/**
 * Check if user can use similar cases feature
 * Returns true if user has access AND has remaining searches
 */
export function canUseSimilarCases(status: UserStatus | null): boolean {
  if (!status) return false;
  return status.similarCasesLimit > 0 && status.similarCasesRemaining > 0;
}

// ============================================================================
// API Endpoints (Direct HTTPS - bypasses bridge)
// ============================================================================

let cachedConfig: AppConfig | null = null;
let cachedPricing: PricingConfig | null = null;

/**
 * Get anonymous trial status (IP-based, no auth required)
 */
export async function getAnonStatus(): Promise<AnonStatus | null> {
  try {
    const response = await fetch(`${API_URL}/anon-status`);
    if (!response.ok) return null;
    return await response.json();
  } catch {
    return null;
  }
}

/**
 * Get app config (public endpoint, no auth required)
 */
export async function getConfig(): Promise<AppConfig | null> {
  if (cachedConfig) return cachedConfig;

  try {
    const response = await fetch(`${API_URL}/config`);
    if (!response.ok) return null;
    cachedConfig = await response.json();
    return cachedConfig;
  } catch {
    return null;
  }
}

/**
 * Get pricing config (public endpoint, no auth required)
 */
export async function getPricing(): Promise<PricingConfig | null> {
  if (cachedPricing) return cachedPricing;

  try {
    const response = await fetch(`${API_URL}/pricing`);
    if (!response.ok) return null;
    cachedPricing = await response.json();
    return cachedPricing;
  } catch {
    return null;
  }
}

export interface SimilarCasesError {
  error: string;
  code: 'FEATURE_NOT_AVAILABLE' | 'SIMILAR_LIMIT_REACHED' | 'UNKNOWN';
  message: string;
}

export interface SimilarCasesResult {
  markdown?: string;
  cases: SimilarCase[];
  cached: boolean;
}

// Legal source type (matches worker)
export type LegalSource = 'canlii' | 'scholar';

/**
 * Get similar cases using AI
 * @param context - First ~10 chunks of case text for LLM analysis
 * @param source - Legal database source ('canlii' or 'scholar', defaults to 'canlii')
 */
export async function getSimilarCases(
  title: string,
  citation: string,
  caseUrl: string,
  context?: string,
  source: LegalSource = 'canlii'
): Promise<{ success: true; data: SimilarCasesResult } | { success: false; error: SimilarCasesError } | null> {
  const token = await getAuthToken(false);

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  try {
    const response = await fetch(`${API_URL}/similar`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ title, citation, caseUrl, context, source }),
    });

    if (!response.ok) {
      // Handle specific error codes
      if (response.status === 403 || response.status === 429) {
        const errorData = await response.json() as SimilarCasesError;
        return { success: false, error: errorData };
      }
      return null;
    }

    const data = await response.json() as SimilarCasesResult;
    return { success: true, data };
  } catch {
    return null;
  }
}

/**
 * Get Stripe Customer Portal URL for subscription management
 */
export async function getPortalUrl(): Promise<string | null> {
  const token = await getAuthToken(false);
  if (!token) return null;

  try {
    const response = await fetch(`${API_URL}/portal`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      return null;
    }

    const data = await response.json() as { url: string };
    return data.url;
  } catch {
    return null;
  }
}

/**
 * Upload student card for verification
 */
export async function verifyStudentStatus(file: File): Promise<{ success: boolean; error?: string }> {
  const token = await getAuthToken(false);
  if (!token) return { success: false, error: 'Not authenticated' };

  try {
    const formData = new FormData();
    formData.append('studentCard', file);

    const response = await fetch(`${API_URL}/student/verify`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: formData,
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      return { success: false, error: data.error || 'Verification failed' };
    }

    return { success: true };
  } catch {
    return { success: false, error: 'Network error' };
  }
}

/**
 * Update user profile (name, userType)
 */
export async function updateProfile(name: string, userType: UserType): Promise<{ success: boolean; error?: string }> {
  const token = await getAuthToken(false);
  if (!token) return { success: false, error: 'Not authenticated' };

  try {
    const response = await fetch(`${API_URL}/profile`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ name, userType }),
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      return { success: false, error: data.error || 'Update failed' };
    }

    return { success: true };
  } catch {
    return { success: false, error: 'Network error' };
  }
}
